import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import Loading from 'core/Loading';
import PageLoadError from 'core/PageLoadError';
import {
  ExperienceEnterpriseEntity,
  ExperienceModuleContextProps,
  ExperienceModuleContextProvider,
  ExperienceVenueEntity,
} from 'modules/experience-core';
import { ExperienceEntity, ExperienceEntityTree, ExperienceEntityType } from 'modules/experience-core';
import { getInitials, recursiveGetOfType } from 'modules/experience-core';

import { SpinnerContainer } from '../Experience.styled';
import { useActiveNavigation } from '../hooks/useActiveNavigation';
import { useEnterprise } from '../hooks/useEnterprise';
import { useSidebar } from '../hooks/useSidebar';
import { IndexPage } from '../pages/IndexPage';

const ContextAwareEnterpriseExperience = ({
  experienceEntity,
  experienceEntityTree,
}: {
  experienceEntity: ExperienceEnterpriseEntity;
  experienceEntityTree: ExperienceEntityTree;
}) => {
  // Naming
  const enterprise = experienceEntity.experienceEntityObject;
  const title = enterprise.name;

  // Navigation
  const { activeNavigation, setActiveNavigation } = useActiveNavigation();
  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const [selectedExperienceEntity, setSelectedExperienceEntity] = useState<ExperienceEntity>(experienceEntity);

  // Venue
  const venueExperienceEntities = recursiveGetOfType(experienceEntityTree, ExperienceEntityType.VENUE).filter(
    experienceVenueEntity => experienceVenueEntity.experienceEntityObject.status === 'VENUE_PUBLISHED',
  );
  const hasVenues = venueExperienceEntities.length > 0;
  const firstExperienceVenueEntity = hasVenues ? venueExperienceEntities[0] : null;
  const [selectedExperienceVenueEntity, setSelectedExperienceVenueEntity] = useState<ExperienceVenueEntity | null>(
    firstExperienceVenueEntity,
  );

  const contextValue: ExperienceModuleContextProps = useMemo(
    () => ({
      experienceEntity,
      experienceEntityTree,
      country: enterprise.country || 'DEU', // check why enterprise.country is optional
      // when no venues are available, the currency and timezone are not available, we
      // should default to some value in this case
      // Usually the brand/enterprise/merchant is broken if there are no venues
      currency: firstExperienceVenueEntity?.experienceEntityObject?.currency || 'EUR',
      timezone: firstExperienceVenueEntity?.experienceEntityObject?.timezone || 'Europe/Helsinki',
      name: enterprise.name,
      address: enterprise.country?.toString() ?? '',
      city: '',
      image: '',
      initials: getInitials(enterprise.name),
      isWoltPlus: false,
      activeNavigation,
      setActiveNavigation,
      isSidebarOpen,
      setIsSidebarOpen,
      selectedExperienceEntity,
      setSelectedExperienceEntity,
      selectedExperienceVenueEntity,
      setSelectedExperienceVenueEntity,
    }),
    [
      enterprise,
      firstExperienceVenueEntity,
      activeNavigation,
      isSidebarOpen,
      selectedExperienceEntity,
      selectedExperienceVenueEntity,
      setActiveNavigation,
      setIsSidebarOpen,
      experienceEntity,
      experienceEntityTree,
      setSelectedExperienceEntity,
      setSelectedExperienceVenueEntity,
    ],
  );

  return (
    <ExperienceModuleContextProvider value={contextValue}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={title} />
      </Helmet>
      <IndexPage />
    </ExperienceModuleContextProvider>
  );
};

export const EnterpriseExperience = ({ experienceEntityId }: { experienceEntityId: string }) => {
  const { experienceEntity, experienceEntityTree, error, isLoading } = useEnterprise(experienceEntityId);

  return (
    <>
      {isLoading && (
        <SpinnerContainer>
          <Loading />
        </SpinnerContainer>
      )}
      {!isLoading && experienceEntity && experienceEntityTree && (
        <ContextAwareEnterpriseExperience
          experienceEntity={experienceEntity}
          experienceEntityTree={experienceEntityTree}
        />
      )}
      {!isLoading && error && <PageLoadError />}
    </>
  );
};
